<template>
  <div class="bg-gray-50">
    <!-- Hero -->
    <section class="bg-black lg:relative bg-grid-white">
      <div class="relative z-10 flex items-center w-full px-6 pt-16 pb-20 mx-auto text-center max-w-7xl lg:py-24 lg:text-left">
        <div class="max-w-xl mx-auto lg:mx-0">
          <h2 class="text-5xl font-extrabold leading-none tracking-tight text-white sm:text-5xl sm:leading-none md:text-6xl">
            Automate &
            <br>
            Scale Your
            <br>
            <span class="text-transparent bg-clip-text bg-gradient-to-r from-indigo-600 to-indigo-400">
               Fleet Operations
            </span>
          </h2>
          <p class="max-w-md pr-4 mx-auto mt-3 text-lg text-gray-300 sm:text-2xl md:mt-5 md:max-w-3xl">
             Save time, reduce costs, drive efficiencies, and serve more customers with a set of tools purpose-built for telematics-enabled fleets.
          </p>
          <div class="mt-10 sm:flex sm:justify-center lg:justify-start">
            <div >
              <router-link :to="{ name: 'Register' }" class="btn btn-primary btn-xl">
                Start a Free Trial
              </router-link>
            </div>
            <div class="mt-3 sm:mt-0 sm:ml-3">
              <router-link :to="{ name: 'Homepage', hash: '#portal' }" class="btn btn-subtle btn-xl">
                Learn More <span class="ml-2 animate-bounce">&darr;</span>
              </router-link>
            </div>
          </div>
        </div>
        <div class="hidden lg:block lg:w-1/2">
          <div class="max-w-xl ml-16">
            <homepage-hero />
          </div>
        </div>
      </div>
    </section>

    <!-- Logos -->
    <div class="bg-gray-50">
      <div class="px-4 py-12 mx-auto max-w-7xl sm:px-6 lg:py-16 lg:px-6">
        <p class="max-w-lg mx-auto text-3xl font-extrabold leading-9 text-center text-gray-800 min-w-2xl">
          Trusted by fleets and businesses of all shapes and sizes
        </p>
        <div class="grid grid-cols-2 gap-1 mt-6 md:grid-cols-3 lg:mt-8">
          <div class="flex items-center justify-center col-span-1 px-8 py-8 bg-white rounded">
            <img class="h-16" src="/images/customers/hfr-logo.png" alt="HFR Transport">
          </div>
          <div class="flex items-center justify-center col-span-1 px-8 py-8 bg-white rounded">
            <img class="h-14" src="/images/customers/g4s-logo.png" alt="G4S">
          </div>
          <div class="flex items-center justify-center col-span-1 px-8 py-8 bg-white rounded">
            <img class="h-16" src="/images/customers/ats-transport-logo.png" alt="ATS Transport">
          </div>
          <div class="flex items-center justify-center col-span-1 px-8 py-8 bg-white rounded">
            <img class="h-16" src="/images/customers/namibia-breweries-logo.png" alt="Namibia Breweries">
          </div>
          <div class="flex items-center justify-center col-span-1 px-8 py-8 bg-white rounded">
            <img class="h-10" src="/images/customers/build-it-logo.png" alt="BuildIt">
          </div>
          <div class="flex items-center justify-center col-span-1 px-8 py-8 bg-white rounded">
            <img class="h-14" src="/images/customers/community-couriers-logo.png" alt="Community Couriers">
          </div>
        </div>
      </div>
    </div>

    <!-- Portal -->
    <section id="portal" class="grid px-6 py-16 mx-auto md:py-20 md:gap-x-12 gap-y-12 md:grid-cols-3 max-w-7xl">
      <div class="space-y-6">
        <div class="flex items-center justify-center leading-tight md:justify-start">
          <img src="@/assets/img/logo-portal.svg" alt="Fleetbeacon Portal" class="flex-none mr-4 -ml-1 h-11">
          <div class="text-lg leading-tight uppercase">
            <div class="font-bold text-gray-900">
              Fleetbeacon
            </div>
            <div class="font-medium text-cyan-500">
              Portal
            </div>
          </div>
        </div>
        <div class="space-y-4 font-medium text-center text-gray-600 md:ml-14 tracking-snug md:text-left">
          <p>
            Streamline your delivery operation from end-to-end with tools to effortlesslty collect, dispatch, and deliver orders while keeping your customers in the loop.
          </p>
          <router-link class="mt-4 btn btn-cyan btn-sm" :to="{ name: 'ProductPortal' }">
            View Portal &rarr;
          </router-link>
        </div>
      </div>

      <div class="col-span-2">
        <div class="grid gap-4">
          <div v-for="feature in portalFeatures" :key="feature.name" class="bg-white rounded-md shadow-sm">
            <div class="flex"> 
              <div class="flex items-center flex-shrink-0 pl-4">
                <div class="flex items-center justify-center flex-none w-16 h-16 rounded-full text-cyan-500 opacity-90">
                  <fa :icon="['fas', feature.icon]" fixed-width class="text-xl" />
                </div>
              </div>
              <div class="p-4">
                <p class="font-bold">
                  {{ feature.name }}
                </p>
                <p class="mt-0.5 text-gray-500">
                  {{ feature.summary }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats -->
    <stats />

    <!-- Radar -->
    <section id="radar" class="grid px-6 py-16 mx-auto md:py-20 md:gap-x-12 gap-y-12 md:grid-cols-3 max-w-7xl">
      <div class="space-y-6">
        <div class="flex items-center justify-center leading-tight md:justify-start">
          <img src="@/assets/img/logo-radar.svg" alt="Fleetbeacon Radar" class="flex-none mr-4 -ml-1 h-11">
          <div class="text-lg leading-tight uppercase">
            <div class="font-bold text-gray-900">
              Fleetbeacon
            </div>
            <div class="font-medium text-blue-500">
              Radar
            </div>
          </div>
        </div>
        <div class="space-y-4 font-medium text-center text-gray-600 md:ml-14 tracking-snug md:text-left">
          <p>
            Save time and money, reduce operational overhead, and automate your risk management with a platform that monitors your fleet 24/7.
          </p>
          <router-link class="mt-4 btn btn-blue btn-sm" :to="{ name: 'ProductRadar' }">
            View Radar &rarr;
          </router-link>
        </div>
      </div>

      <div class="col-span-2">
        <div class="grid gap-4">
          <div v-for="feature in radarFeatures" :key="feature.name" class="bg-white rounded-md shadow-sm">
            <div class="flex"> 
              <div class="flex items-center flex-shrink-0 pl-4">
                <div class="flex items-center justify-center flex-none w-16 h-16 text-blue-500 rounded-full opacity-90">
                  <fa :icon="['fas', feature.icon]" fixed-width class="text-xl" />
                </div>
              </div>
              <div class="p-4">
                <p class="font-bold">
                  {{ feature.name }}
                </p>
                <p class="mt-0.5 text-gray-500">
                  {{ feature.summary }}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Testimonial -->
    <section class="py-12 overflow-hidden bg-gray-50 md:py-20 lg:py-24">
      <div class="relative max-w-screen-xl px-4 mx-auto sm:px-6 lg:px-8">
        <div class="relative">
          <img src="/images/customers/ats-transport-logo.png" alt="ATS Transport" class="h-20 mx-auto">
          <blockquote class="mt-8">
            <div class="max-w-3xl mx-auto text-2xl font-medium leading-9 text-center text-gray-900">
              <p>
                &ldquo;Fleetbeacon has saved us so many hours every month with their auto-generated reports to help with our compliance. It's worth every penny.&rdquo;
              </p>
            </div>
            <footer class="mt-8">
              <div class="md:flex md:items-center md:justify-center">
                <div class="md:flex-shrink-0">
                  <img class="w-10 h-10 mx-auto rounded-full" src="/images/customers/jacques-geldenhuys.jpg" alt="">
                </div>
                <div class="mt-3 text-center md:mt-0 md:ml-4 md:text-left">
                  <div class="text-base font-medium leading-6 text-gray-900">Jacques Geldenhuys</div>
                  <div class="text-base font-medium leading-6 text-gray-400">ATS Transport</div>
                </div>
              </div>
            </footer>
          </blockquote>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
  import HomepageHero from '@/views/site/HomepageHero'
  import Stats from '@/views/site/Stats'
  import { features as portalFeatures } from '@/store/products/portal'
  import { features as radarFeatures } from '@/store/products/radar'

  export default {
    name: 'Homepage',

    metaInfo: {
      title: 'Last-Mile Software for Local Service and Delivery Fleets'
    },

    components: {
      HomepageHero,
      Stats
    },

    data () {
      return {
        portalFeatures,
        radarFeatures
      }
    }
  }
</script>